import { useEffect, useMemo, useRef, useState } from "react";
import api, { eur } from "@/lib/api";
import { useBrand } from "@/context/BrandContext";
import JsBarcode from "jsbarcode";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Printer, Tags, Loader2 } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

function addDays(dateStr, days) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + Number(days || 0));
  return d;
}

const fmtDate = (d) =>
  d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });

const LABEL_FORMATS = [
  { id: "a6", label: "A6 · 105×148 mm", w: 105, h: 148 },
  { id: "a7", label: "A7 · 74×105 mm", w: 74, h: 105 },
  { id: "100x70", label: "100×70 mm", w: 100, h: 70 },
  { id: "100x50", label: "100×50 mm", w: 100, h: 50 },
  { id: "60x40", label: "60×40 mm", w: 60, h: 40 },
  { id: "50x30", label: "50×30 mm", w: 50, h: 30 },
];

export default function Labels() {
  const { brand } = useBrand();
  const [recipes, setRecipes] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selId, setSelId] = useState("");
  const [prodDate, setProdDate] = useState(today());
  const [lotto, setLotto] = useState("");
  const [autoLot, setAutoLot] = useState(true);
  const [formatId, setFormatId] = useState("a6");

  useEffect(() => {
    Promise.all([api.get("/recipes"), api.get("/ingredients")])
      .then(([r, i]) => {
        setRecipes(r.data);
        setIngredients(i.data);
        if (r.data.length) setSelId(r.data[0].id);
      })
      .finally(() => setLoading(false));
  }, []);

  const ingMap = useMemo(() => Object.fromEntries(ingredients.map((i) => [i.id, i])), [ingredients]);
  const recipe = recipes.find((r) => r.id === selId);
  const expiry = recipe ? addDays(prodDate, recipe.shelf_life_days) : null;
  const fmt = LABEL_FORMATS.find((f) => f.id === formatId) || LABEL_FORMATS[0];
  const barcodeRef = useRef(null);

  const autoLotValue = recipe
    ? `${prodDate.replaceAll("-", "").slice(2)}-${(recipe.name || "").replace(/[^a-zA-Z0-9]/g, "").slice(0, 3).toUpperCase() || "XXX"}`
    : "";
  const effectiveLot = autoLot ? autoLotValue : lotto;
  const storageText = recipe?.storage_conditions || brand.label_storage_default;
  const usageText = recipe?.usage_instructions || brand.label_usage_default;

  useEffect(() => {
    if (recipe?.ean && barcodeRef.current) {
      const isEan13 = /^\d{13}$/.test(recipe.ean.trim());
      try {
        JsBarcode(barcodeRef.current, recipe.ean.trim(), {
          format: isEan13 ? "EAN13" : "CODE128",
          width: 1.6,
          height: 45,
          fontSize: 13,
          margin: 0,
          background: "#ffffff",
        });
      } catch (e) {
        try {
          JsBarcode(barcodeRef.current, recipe.ean.trim(), { format: "CODE128", width: 1.6, height: 45, fontSize: 13, margin: 0 });
        } catch (e2) {}
      }
    }
  }, [recipe, selId]);

  const ingredientNames = recipe
    ? recipe.items
        .map((it) => ingMap[it.ingredient_id]?.name)
        .filter(Boolean)
        .join(", ")
    : "";

  if (loading)
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );

  return (
    <div className="space-y-6" data-testid="labels-page">
      <div className="no-print">
        <h1 className="font-display text-4xl font-extrabold tracking-tight">Etichette</h1>
        <p className="text-muted-foreground mt-1">Genera e stampa l'etichetta con scadenza e allergeni</p>
      </div>

      {recipes.length === 0 ? (
        <Card className="p-12 text-center border-dashed no-print">
          <Tags className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">Crea prima una ricetta per generare l'etichetta.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Card className="p-6 border-border no-print space-y-4">
            <div className="space-y-1.5">
              <Label>Prodotto</Label>
              <Select value={selId} onValueChange={setSelId}>
                <SelectTrigger data-testid="label-recipe-select">
                  <SelectValue placeholder="Seleziona prodotto" />
                </SelectTrigger>
                <SelectContent>
                  {recipes.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Data di produzione</Label>
                <Input type="date" value={prodDate} onChange={(e) => setProdDate(e.target.value)} data-testid="label-proddate-input" />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>Lotto</Label>
                  <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                    <input type="checkbox" checked={autoLot} onChange={(e) => setAutoLot(e.target.checked)} data-testid="label-autolot-checkbox" />
                    Auto
                  </label>
                </div>
                <Input
                  value={autoLot ? autoLotValue : lotto}
                  onChange={(e) => setLotto(e.target.value)}
                  disabled={autoLot}
                  placeholder="L-001"
                  data-testid="label-lotto-input"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Formato etichetta</Label>
              <Select value={formatId} onValueChange={setFormatId}>
                <SelectTrigger data-testid="label-format-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LABEL_FORMATS.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {recipe && (
              <p className="text-sm text-muted-foreground">
                Conservazione: <strong>{recipe.shelf_life_days} giorni</strong> · Scadenza:{" "}
                <strong>{expiry && fmtDate(expiry)}</strong>
              </p>
            )}
            <Button onClick={() => window.print()} className="rounded-full gap-2 w-full" data-testid="print-label-btn">
              <Printer className="w-4 h-4" /> Stampa etichetta
            </Button>
          </Card>

          {/* label preview */}
          {recipe && (
            <div id="print-label" className="flex justify-center items-start">
              <style
                dangerouslySetInnerHTML={{
                  __html: `@media print { @page { size: ${fmt.w}mm ${fmt.h}mm; margin: 4mm; } }`,
                }}
              />
              <div
                className="bg-white text-black border-2 border-black rounded-lg p-4"
                style={{ width: `${fmt.w}mm`, minHeight: `${fmt.h}mm` }}
                data-testid="label-preview"
              >
                <div className="flex items-center justify-center gap-2 pb-2 mb-2">
                  {brand.logo && <img src={brand.logo} alt="logo" className="h-6 w-auto object-contain" />}
                  <span className="text-[11px] font-bold uppercase tracking-wide">{brand.business_name}</span>
                </div>
                <div className="text-center border-b-2 border-black pb-2 mb-3">
                  <h2 className="font-display font-extrabold text-2xl leading-tight uppercase">{recipe.name}</h2>
                </div>

                <div className="text-[11px] leading-snug mb-3">
                  <p className="font-bold uppercase mb-0.5">Ingredienti:</p>
                  <p>{ingredientNames || "—"}</p>
                </div>

                {recipe.allergens?.length > 0 && (
                  <div className="text-[11px] mb-3">
                    <span className="font-bold uppercase">Allergeni: </span>
                    <span className="font-bold uppercase">{recipe.allergens.join(", ")}</span>
                  </div>
                )}

                <table className="w-full text-[11px] border border-black mb-3">
                  <thead>
                    <tr className="border-b border-black">
                      <th className="text-left p-1 font-bold">Val. medi</th>
                      <th className="text-right p-1 font-bold">100 g</th>
                      <th className="text-right p-1 font-bold">Porz.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Energia (kcal)", "kcal"],
                      ["Grassi (g)", "fat"],
                      ["di cui saturi (g)", "saturated"],
                      ["Carboidrati (g)", "carbs"],
                      ["di cui zuccheri (g)", "sugar"],
                      ["Fibre (g)", "fiber"],
                      ["Proteine (g)", "protein"],
                      ["Sale (g)", "salt"],
                    ].map(([lbl, key]) => (
                      <tr key={key} className="border-b border-black/30">
                        <td className="p-1">{lbl}</td>
                        <td className="p-1 text-right font-mono">{recipe.nutrition_per_100g?.[key] ?? 0}</td>
                        <td className="p-1 text-right font-mono">{recipe.nutrition_per_portion?.[key] ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="flex justify-between text-[11px] border-t-2 border-black pt-2">
                  {brand.label_show_production_date && (
                    <div>
                      <p className="font-bold uppercase">Prodotto il</p>
                      <p className="font-mono">{fmtDate(new Date(prodDate))}</p>
                    </div>
                  )}
                  <div className={brand.label_show_production_date ? "text-right" : ""}>
                    <p className="font-bold uppercase">Da consumarsi entro</p>
                    <p className="font-mono font-bold">{expiry && fmtDate(expiry)}</p>
                  </div>
                </div>
                {effectiveLot && <p className="text-[10px] mt-2" data-testid="label-lotto">Lotto: {effectiveLot}</p>}

                {(storageText || usageText) && (
                  <div className="text-[10px] leading-tight mt-2" data-testid="label-legal">
                    {storageText && (
                      <p>
                        <span className="font-bold uppercase">Conservazione: </span>
                        {storageText}
                      </p>
                    )}
                    {usageText && (
                      <p>
                        <span className="font-bold uppercase">Modalità d'uso: </span>
                        {usageText}
                      </p>
                    )}
                  </div>
                )}

                {recipe.ean && (
                  <div className="flex justify-center mt-3">
                    <svg ref={barcodeRef} data-testid="label-barcode" />
                  </div>
                )}

                {(brand.producer_name || brand.producer_address || brand.vat_number || brand.contacts) && (
                  <div className="text-[9px] leading-tight mt-3 border-t border-black/40 pt-2" data-testid="label-producer">
                    {brand.producer_name && <p className="font-bold uppercase">{brand.producer_name}</p>}
                    {brand.producer_address && <p>{brand.producer_address}</p>}
                    {brand.vat_number && <p>P.IVA {brand.vat_number}</p>}
                    {brand.contacts && <p>{brand.contacts}</p>}
                  </div>
                )}

                <p className="text-[9px] text-center mt-2 uppercase tracking-wider">Peso: {recipe.final_weight ?? recipe.total_weight} g · {recipe.portions} porzioni</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
